// Vue « Loges » : La Loge, l'espace client d'ACTE.5, vu du studio. Trois gestes : créer le projet, glisser les
// fichiers, demander une décision. Ici : la vue d'ensemble (boîte de réception, maisons et projets) et une maison
// (personnes, projets, déblocage des téléchargements). Le projet lui-même : logeProject.ts.
// Les données ne passent pas par le Store de World : elles viennent des fonctions /api/loge/studio/* (docs/LOGE-API.md).
import '@fontsource-variable/bodoni-moda/opsz.css';
import '../styles/loge.css';
import { initials, stepLabel } from '../core/loge';
import { logeStudio, type InboxItem, type StudioClient, type StudioOverview, type StudioProjectSummary } from '../data/loge';
import type { Store } from '../data/store';
import type { MountedView } from './contacts';
import { el, esc, on } from './dom';
import { icon } from './icons';
import { openDeleteDialog, openInviteLink, openMember, openNewClient, openNewProject } from './logeDialogs';
import { fmtLong, licon, mailToast, plural, stepNo } from './logeKit';
import { mountProjectView, type ProjectView } from './logeProject';
import { syncBanner } from './shell';
import { showToast } from './toast';

export type LogeRoute =
  | { name: 'overview' }
  | { name: 'client'; clientId: string }
  | { name: 'project'; projectId: string; folderId?: string; fileId?: string };

export interface LogeFocus { projectId?: string; clientId?: string }

// La boîte de réception n'a pas de « non lu » côté serveur (60 derniers jours renvoyés) : World garde, sur cet
// appareil, la date de la dernière lecture complète et les lignes lues une à une depuis.
const READ_KEY = 'a5w-loge-inbox';
interface ReadState { at: number; ids: string[] }
function readState(): ReadState {
  try {
    const v = JSON.parse(localStorage.getItem(READ_KEY) ?? 'null') as Partial<ReadState> | null;
    return { at: typeof v?.at === 'number' ? v.at : 0, ids: Array.isArray(v?.ids) ? v.ids.filter((x): x is string => typeof x === 'string') : [] };
  } catch { return { at: 0, ids: [] }; }
}
function saveRead(s: ReadState): void {
  try { localStorage.setItem(READ_KEY, JSON.stringify({ at: s.at, ids: s.ids.slice(-300) })); } catch { /* stockage indisponible */ }
}

export function mountLoge(main: HTMLElement, store: Store, focus?: LogeFocus): MountedView {
  let route: LogeRoute = focus?.projectId ? { name: 'project', projectId: focus.projectId } : focus?.clientId ? { name: 'client', clientId: focus.clientId } : { name: 'overview' };
  let overview: StudioOverview | null = null;
  let overviewError: string | null = null;
  let loading: Promise<void> | null = null;
  let project: ProjectView | null = null;
  /** Réponses tapées dans la boîte de réception, gardées d'un rafraîchissement à l'autre. */
  const drafts = new Map<string, string>();
  let read = readState();
  const isUnread = (i: InboxItem): boolean => (Date.parse(i.createdAt) || 0) > read.at && !read.ids.includes(i.id);
  function markRead(ids: string[]): void {
    const fresh = ids.filter((id) => id && !read.ids.includes(id));
    if (!fresh.length) return;
    read = { at: read.at, ids: [...read.ids, ...fresh] };
    saveRead(read);
  }

  main.innerHTML = `<div class="loge"><div data-banner></div><div data-view></div></div>`;
  const view = el(main, '[data-view]');

  function loadOverview(): Promise<void> {
    loading ??= logeStudio.overview()
      .then((o) => { overview = o; overviewError = null; })
      .catch((e: unknown) => { overviewError = e instanceof Error ? e.message : String(e); })
      .finally(() => { loading = null; });
    return loading;
  }

  async function refresh(): Promise<void> {
    await loadOverview();
    if (route.name !== 'project') render();
    else project?.setOverview(overview);
  }

  function go(r: LogeRoute): void {
    route = r;
    render();
    window.scrollTo({ top: 0 });
  }

  function findClient(id: string): StudioClient | undefined {
    return overview?.clients.find((c) => c.id === id);
  }

  function render(): void {
    if (route.name !== 'project' && project) { project.destroy(); project = null; }
    if (route.name === 'project') {
      if (project && project.projectId === route.projectId) { project.focus(route.folderId, route.fileId); return; }
      project?.destroy();
      project = mountProjectView(view, { projectId: route.projectId, folderId: route.folderId ?? null, fileId: route.fileId ?? null, overview, go, refreshOverview: () => void refresh() });
      return;
    }
    if (!overview) {
      view.innerHTML = overviewError
        ? errorBlock(overviewError)
        : `<section class="page-head"><div><div class="eyebrow">La Loge · espace client</div><h1>Loges</h1></div></section><div class="panel"><div class="loading-rows"><div class="skeleton"></div><div class="skeleton"></div><div class="skeleton"></div></div></div>`;
      if (!overviewError && !loading) void refresh();
      return;
    }
    if (route.name === 'client') {
      const c = findClient(route.clientId);
      if (!c) { route = { name: 'overview' }; render(); return; }
      view.innerHTML = clientView(c);
      return;
    }
    view.innerHTML = overviewView(overview);
    // Une réponse en cours de frappe survit au rafraîchissement.
    view.querySelectorAll<HTMLInputElement>('[data-reply-input]').forEach((i) => { i.value = drafts.get(i.dataset.replyInput ?? '') ?? ''; });
  }

  // ── Vue d'ensemble ────────────────────────────────────────────────────────────────────────────

  function overviewView(o: StudioOverview): string {
    const inbox = o.inbox.filter(isUnread);
    const seen = o.inbox.filter((i) => !isUnread(i));
    return `
      <section class="page-head"><div><div class="eyebrow">La Loge · espace client</div><h1>Loges</h1><p>Les maisons, leurs projets, et ce que les clients vous ont laissé.</p></div>
        <div class="page-actions"><button class="btn" type="button" data-refresh title="Relire">${icon('refresh')}<span class="sr">Relire</span></button><button class="btn primary" type="button" data-new-client>${icon('plus')}Nouvelle maison</button></div></section>
      <section class="loge-block" aria-labelledby="lgInboxTitle">
        <div class="loge-sec-head"><h2 id="lgInboxTitle">À traiter</h2>${inbox.length ? `<span class="loge-count">${inbox.length}</span><button class="btn small ghost loge-sec-act" type="button" data-read-all>${licon('check')}Tout marquer comme lu</button>` : ''}</div>
        ${inbox.length ? `<div class="loge-inbox">${inbox.map((i) => inboxItem(i, true)).join('')}</div>` : `<p class="loge-empty-line">${licon('check')}Rien de nouveau. Les notes, demandes d’appel et envois des clients arriveront ici.</p>`}
        ${seen.length ? `<details class="loge-seen"><summary>Déjà lus · ${seen.length} sur les 60 derniers jours</summary><div class="loge-inbox">${seen.map((i) => inboxItem(i, false)).join('')}</div></details>` : ''}
        ${waitingList(o)}
      </section>
      <section class="loge-block" aria-labelledby="lgHousesTitle">
        <div class="loge-sec-head"><h2 id="lgHousesTitle">Maisons</h2>${o.clients.length ? `<span class="loge-count muted">${o.clients.length}</span>` : ''}</div>
        ${o.clients.length ? `<div class="loge-houses">${o.clients.map(houseCard).join('')}</div>` : `
          <div class="panel"><div class="empty">${licon('key')}<h3>Aucune maison pour l’instant</h3><p>Créez la maison d’un client, ajoutez ses personnes, puis son premier projet : les dossiers de chaque étape sont prêts tout de suite.</p><div class="actions"><button class="btn primary" type="button" data-new-client>${icon('plus')}Nouvelle maison</button></div></div></div>`}
      </section>`;
  }

  /** Phrase d'une ligne : celle du serveur, le nom de la personne en gras ; sinon une phrase composée ici. */
  function phrase(i: InboxItem): string {
    const who = i.memberName || 'Le client';
    const about = i.proposalTitle ? `« ${i.proposalTitle} »${i.round && i.round > 1 ? ` · round ${i.round}` : ''}` : 'une proposition';
    const text = i.text || (i.kind === 'note' ? `${who} a laissé une note sur ${about}`
      : i.kind === 'call' ? `${who} souhaite en parler · ${about}`
      : i.kind === 'approve' ? `${who} a validé ${about}`
      : i.kind === 'upload' ? `${who} a envoyé « ${i.fileName ?? 'un fichier'} »` : who);
    return i.memberName && text.startsWith(i.memberName) ? `<b>${esc(i.memberName)}</b>${esc(text.slice(i.memberName.length))}` : esc(text);
  }

  function inboxItem(i: InboxItem, unread: boolean): string {
    const where = [i.projectName, i.clientName, fmtLong(i.createdAt)].filter(Boolean).map(esc).join(' · ');
    const mark = unread ? `<button class="icon-btn loge-mark" type="button" data-read="${esc(i.id)}" title="Marquer comme lu" aria-label="Marquer comme lu">${licon('check')}</button>` : '';
    const open = `<div class="loge-in-act"><button class="btn small ghost" type="button" data-open-project="${esc(i.projectId)}" data-read-id="${esc(i.id)}">Ouvrir le projet</button>${mark}</div>`;
    if (i.kind === 'note') {
      return `<article class="loge-in note">
        <span class="loge-av" aria-hidden="true">${esc(initials(i.memberName || '?'))}</span>
        <div class="loge-in-body">
          <p class="loge-in-meta">${phrase(i)} <small>· ${where}</small></p>
          ${i.body ? `<blockquote>${esc(i.body)}</blockquote>` : ''}
          ${i.proposalId ? `<form class="loge-reply" data-reply="${esc(i.proposalId)}" data-read-id="${esc(i.id)}"><input type="text" maxlength="600" placeholder="Répondre en une ligne…" aria-label="Réponse à ${esc(i.memberName || 'la note')}" data-reply-input="${esc(i.proposalId)}"><button class="btn small primary" type="submit">Répondre</button></form>` : ''}
        </div>${open}
      </article>`;
    }
    if (i.kind === 'upload') {
      return `<article class="loge-in upload">
        <span class="loge-th" aria-hidden="true">${i.thumb ? `<img src="${esc(i.thumb)}" alt="" loading="lazy" data-thumb>` : licon('file')}</span>
        <div class="loge-in-body"><p>${phrase(i)} <small>· ${where}</small></p></div>
        <div class="loge-in-act"><button class="btn small primary" type="button" data-open-file="${esc(i.projectId)}" data-file="${esc(i.fileId ?? '')}" data-read-id="${esc(i.id)}">Voir</button>${mark}</div>
      </article>`;
    }
    const ic = i.kind === 'call' ? 'phone' : i.kind === 'approve' ? 'check' : 'info';
    return `<article class="loge-in ${i.kind}">
      <span class="loge-th icon" aria-hidden="true">${licon(ic)}</span>
      <div class="loge-in-body"><p>${phrase(i)} <small>· ${where}</small></p>${i.body ? `<blockquote>${esc(i.body)}</blockquote>` : ''}${i.kind === 'call' ? '<small class="hint">Le client a reçu le lien de prise de rendez-vous du site.</small>' : ''}</div>
      ${open}
    </article>`;
  }

  function waitingList(o: StudioOverview): string {
    if (!o.waiting.length) return '';
    const day = 86_400_000;
    return `<div class="loge-waiting"><span class="eyebrow">En attente du client</span><ul>${o.waiting.map((w) => {
      const days = Math.floor((Date.now() - Date.parse(w.createdAt)) / day);
      const since = !Number.isFinite(days) ? '' : days <= 0 ? 'envoyée aujourd’hui' : days === 1 ? 'depuis hier' : `depuis ${days} jours`;
      return `<li><button type="button" data-open-project="${esc(w.projectId)}"><span><b>${esc(w.title)}</b>${w.round > 1 ? ` · round ${w.round}` : ''} · ${esc(w.projectName)}${w.clientName ? ` · ${esc(w.clientName)}` : ''}</span><small>${esc(since)}</small></button></li>`;
    }).join('')}</ul></div>`;
  }

  function miniSteps(p: StudioProjectSummary): string {
    const n = p.steps.length || 6;
    return `<span class="loge-mini" aria-hidden="true">${Array.from({ length: n }, (_, k) => `<i class="${k + 1 < p.currentStep ? 'done' : k + 1 === p.currentStep ? 'now' : ''}"></i>`).join('')}</span>`;
  }

  function projectRow(p: StudioProjectSummary): string {
    const step = p.steps[p.currentStep - 1];
    return `<li><button class="loge-prj" type="button" data-open-project="${esc(p.id)}">
      <span class="loge-prj-no">${stepNo(p.number)}</span>
      <span class="loge-prj-name"><b>${esc(p.name)}</b><small>${esc(p.kind || 'Projet')}${p.status !== 'active' ? ` · ${p.status === 'completed' ? 'terminé' : 'archivé'}` : ''}</small></span>
      <span class="loge-prj-step">${miniSteps(p)}<small>${step ? `${stepNo(p.currentStep)} · ${esc(stepLabel(step, 'fr'))}` : `Étape ${p.currentStep}`}</small></span>
      <span class="loge-prj-flags">${p.pending ? `<span class="loge-tag wait">${p.pending > 1 ? `${p.pending} décisions en attente` : '1 décision en attente'}</span>` : ''}${p.downloadsUnlocked ? `<span class="loge-tag ok">${licon('unlock')}Débloqué</span>` : ''}</span>
      ${icon('chevron', 'loge-chev')}
    </button></li>`;
  }

  function houseCard(c: StudioClient): string {
    const active = c.members.filter((m) => !m.revokedAt);
    return `<article class="loge-house">
      <header>
        <button class="loge-house-name" type="button" data-open-client="${esc(c.id)}"><h3>${esc(c.name)}</h3>${icon('chevron')}</button>
        <p class="loge-house-meta">${c.archivedAt ? '<span class="loge-tag">Fermée</span> · ' : ''}${c.lang === 'en' ? 'English' : 'Français'} · ${active.length ? esc(active.map((m) => m.name || m.email).join(', ')) : 'personne d’invité'}${c.downloadsUnlocked ? ` · ${licon('unlock')}téléchargements débloqués` : ''}</p>
      </header>
      ${c.projects.length ? `<ul class="loge-prj-list">${c.projects.map(projectRow).join('')}</ul>` : '<p class="loge-house-empty">Aucun projet pour l’instant.</p>'}
      <footer><button class="btn small ghost" type="button" data-open-client="${esc(c.id)}">${icon('users')}Personnes et accès</button><button class="btn small" type="button" data-new-project="${esc(c.id)}">${icon('plus')}Nouveau projet</button></footer>
    </article>`;
  }

  // ── Une maison ────────────────────────────────────────────────────────────────────────────────

  function clientView(c: StudioClient): string {
    const active = c.members.filter((m) => !m.revokedAt);
    const names = new Map(c.projects.map((p) => [p.id, p.name]));
    const person = (m: StudioClient['members'][number]) => {
      const access = m.projectIds === null ? 'Tous les projets' : m.projectIds.map((id) => names.get(id) ?? 'projet retiré').join(', ') || 'Aucun projet';
      const seen = m.revokedAt ? `Accès retiré le ${fmtLong(m.revokedAt)}`
        : m.lastSeenAt ? `Dernière visite le ${fmtLong(m.lastSeenAt)}`
        : m.invitedAt ? `Invitation envoyée le ${fmtLong(m.invitedAt)}`
        : m.createdAt ? `Ajoutée le ${fmtLong(m.createdAt)}` : '';
      return `<div class="loge-person${m.revokedAt ? ' revoked' : ''}">
        <span class="loge-av" aria-hidden="true">${esc(m.initials)}</span>
        <div class="loge-person-who"><span class="loge-person-line"><b>${esc(m.name || m.email)}</b>${m.role ? `<span class="role-tag">${esc(m.role)}</span>` : ''}</span>
          <small>${esc(m.email)}${m.lang && m.lang !== c.lang ? ` · ${m.lang === 'en' ? 'English' : 'Français'}` : ''}</small>
          <small>${esc([access, seen].filter(Boolean).join(' · '))}</small></div>
        <div class="loge-person-actions">${m.revokedAt ? '' : `<button class="btn small" type="button" data-invite="${esc(m.id)}">${icon('mail')}${m.invitedAt ? 'Renvoyer l’invitation' : 'Envoyer l’invitation'}</button>`}
          <button class="icon-btn" type="button" data-edit-member="${esc(m.id)}" title="Modifier" aria-label="Modifier ${esc(m.name || m.email)}">${icon('edit')}</button>
          ${m.revokedAt ? '' : `<button class="icon-btn loge-danger" type="button" data-revoke="${esc(m.id)}" title="Retirer l’accès" aria-label="Retirer l’accès de ${esc(m.name || m.email)}">${icon('close')}</button>`}</div>
      </div>`;
    };
    return `
      <nav class="loge-path" aria-label="Fil d’Ariane"><button type="button" data-go-overview>Loges</button><span aria-hidden="true">›</span><b>${esc(c.name)}</b></nav>
      <section class="page-head"><div><div class="eyebrow">Maison · ${c.lang === 'en' ? 'English' : 'Français'}</div><h1>${esc(c.name)}</h1>
        <p>${plural(active.length, 'personne', 'personnes')} · ${plural(c.projects.length, 'projet', 'projets')} · ${c.downloadsUnlocked ? 'téléchargements débloqués' : 'consultation seule jusqu’au règlement'}</p></div>
        <div class="page-actions">${c.downloadsUnlocked ? `<span class="loge-tag ok big">${licon('unlock')}Téléchargements débloqués</span><button class="btn ghost" type="button" data-unlock-client="${esc(c.id)}" data-lock="true">${licon('lock')}Refermer</button>` : `<button class="btn" type="button" data-unlock-client="${esc(c.id)}">${licon('unlock')}Débloquer les téléchargements</button>`}
          <button class="btn primary" type="button" data-new-project="${esc(c.id)}">${icon('plus')}Nouveau projet</button></div></section>
      <div class="loge-client">
        <section class="card loge-card">
          <div class="loge-card-head"><h2>Projets</h2></div>
          ${c.projects.length ? `<ul class="loge-prj-list flush">${c.projects.map(projectRow).join('')}</ul>` : `<p class="hint">Aucun projet. Le modèle « Montre » crée les six étapes et un dossier par étape, plus « Vos envois » pour le client.</p><div><button class="btn primary" type="button" data-new-project="${esc(c.id)}">${icon('plus')}Nouveau projet</button></div>`}
        </section>
        <section class="card loge-card">
          <div class="loge-card-head"><h2>Personnes</h2><button class="btn small" type="button" data-add-member="${esc(c.id)}">${icon('plus')}Ajouter</button></div>
          ${c.members.length ? `<div class="loge-people">${[...c.members].sort((a, b) => Number(!!a.revokedAt) - Number(!!b.revokedAt)).map(person).join('')}</div>` : '<p class="hint">Personne pour l’instant. Ajoutez les personnes de la maison qui suivront ses projets : elles se connectent par un lien reçu par e-mail.</p>'}
          <p class="hint">Chaque invitation contient un lien de connexion valable 15 minutes ; la personne peut en redemander un à tout moment depuis La Loge.</p>
        </section>
        <section class="card loge-card loge-life">
          <div class="loge-card-head"><h2>Cette maison</h2></div>
          ${c.archivedAt
            ? `<p class="hint">Maison fermée depuis le ${esc(fmtLong(c.archivedAt))} : ses personnes ne peuvent plus se connecter. Rien n’est perdu.</p>
               <button class="btn small" type="button" data-archive-client="${esc(c.id)}" data-on="0">${icon('restore')}Rouvrir la maison</button>`
            : `<p class="hint">La fermer empêche ses personnes de se connecter et retire ses projets de leur vue. Rien n’est perdu : vous pouvez la rouvrir.</p>
               <button class="btn small" type="button" data-archive-client="${esc(c.id)}" data-on="1">${icon('check')}Fermer la maison</button>`}
          <p class="hint loge-life-sep">La supprimer efface la maison, ses personnes et ${c.projects.length === 1 ? 'son projet' : c.projects.length ? `ses ${c.projects.length} projets` : 'ses projets'}, jusqu’aux fichiers dans le stockage. Sans retour.</p>
          <button class="btn small danger" type="button" data-delete-client="${esc(c.id)}">${icon('trash')}Supprimer définitivement</button>
        </section>
      </div>`;
  }

  // ── Événements ────────────────────────────────────────────────────────────────────────────────

  on(view, 'click', '[data-go-overview]', () => go({ name: 'overview' }));
  on(view, 'click', '[data-refresh]', (_e, t) => {
    (t as HTMLButtonElement).disabled = true;
    void refresh().then(() => { if (overviewError) showToast({ id: 0, kind: 'error', text: overviewError }); });
  });
  on(view, 'click', '[data-retry-load]', () => { overviewError = null; render(); });
  on(view, 'click', '[data-open-client]', (_e, t) => go({ name: 'client', clientId: t.dataset.openClient! }));
  on(view, 'click', '[data-open-project]', (_e, t) => { if (t.dataset.readId) markRead([t.dataset.readId]); if (t.dataset.openProject) go({ name: 'project', projectId: t.dataset.openProject }); });
  on(view, 'click', '[data-read]', (_e, t) => { markRead([t.dataset.read!]); render(); });
  on(view, 'click', '[data-read-all]', () => {
    read = { at: Math.max(Date.now(), ...((overview?.inbox ?? []).map((i) => Date.parse(i.createdAt) || 0))), ids: [] };
    saveRead(read);
    render();
  });
  on(view, 'click', '[data-open-file]', (_e, t) => { if (t.dataset.readId) markRead([t.dataset.readId]); if (t.dataset.openFile) go({ name: 'project', projectId: t.dataset.openFile, ...(t.dataset.file ? { fileId: t.dataset.file } : {}) }); });
  on(view, 'click', '[data-archive-client]', (_e, t) => {
    const c = findClient(t.dataset.archiveClient!);
    if (!c) return;
    const fermer = t.dataset.on === '1';
    (t as HTMLButtonElement).disabled = true;
    void logeStudio.archiveClient(c.id, fermer)
      .then(() => {
        showToast({ id: 0, kind: 'info', text: fermer ? `« ${c.name} » est fermée : ses personnes ne peuvent plus se connecter.` : `« ${c.name} » est rouverte.` });
        return loadOverview().then(render);
      })
      .catch((e: unknown) => showToast({ id: 0, kind: 'error', text: e instanceof Error ? e.message : 'État non enregistré.' }))
      .finally(() => { (t as HTMLButtonElement).disabled = false; });
  });
  on(view, 'click', '[data-delete-client]', (_e, t) => {
    const c = findClient(t.dataset.deleteClient!);
    if (!c) return;
    const contents = ([[c.projects.length, 'projet', 'projets'], [c.members.length, 'personne', 'personnes']] as const)
      .filter(([n]) => n > 0).map(([n, s, pl]) => plural(n, s, pl)).join(', ') || 'la maison et ses réglages';
    openDeleteDialog({
      kind: 'maison',
      name: c.name,
      contents,
      onDelete: async (confirm) => {
        const r = await logeStudio.deleteClient(c.id, confirm);
        showToast({
          id: 0, kind: r.storageError ? 'error' : 'info',
          text: r.storageError
            ? `« ${c.name} » est supprimée, mais ses fichiers n’ont pas tous pu être effacés du stockage.`
            : `« ${c.name} » est supprimée, avec ${plural(r.projects, 'projet', 'projets')} et ${plural(r.files, 'fichier', 'fichiers')}.`,
        });
        await loadOverview();
        go({ name: 'overview' });
      },
    });
  });
  on(view, 'click', '[data-new-client]', () => openNewClient((id) => {
    showToast({ id: 0, kind: 'info', text: 'Maison créée.' });
    void loadOverview().then(() => go(id ? { name: 'client', clientId: id } : { name: 'overview' }));
  }));
  on(view, 'click', '[data-new-project]', (_e, t) => {
    const c = findClient(t.dataset.newProject!);
    if (!c) return;
    openNewProject(c, (id) => {
      showToast({ id: 0, kind: 'info', text: 'Projet créé, avec un dossier par étape.' });
      void loadOverview().then(() => go(id ? { name: 'project', projectId: id } : { name: 'client', clientId: c.id }));
    });
  });
  on(view, 'click', '[data-add-member]', (_e, t) => {
    const c = findClient(t.dataset.addMember!);
    if (c) openMember(c, null, () => { showToast({ id: 0, kind: 'info', text: 'Personne ajoutée. Envoyez-lui l’invitation quand sa Loge est prête.' }); void refresh(); });
  });
  on(view, 'click', '[data-edit-member]', (_e, t) => {
    const c = route.name === 'client' ? findClient(route.clientId) : undefined;
    const m = c?.members.find((x) => x.id === t.dataset.editMember);
    if (c && m) openMember(c, m, () => { showToast({ id: 0, kind: 'info', text: 'Enregistré.' }); void refresh(); });
  });
  on(view, 'click', '[data-invite]', (_e, t) => {
    const c = route.name === 'client' ? findClient(route.clientId) : undefined;
    const m = c?.members.find((x) => x.id === t.dataset.invite);
    if (!c || !m) return;
    if (!confirm(`Envoyer à ${m.email} l’e-mail « Votre Loge est ouverte », avec un lien de connexion ?`)) return;
    const btn = t as HTMLButtonElement;
    btn.disabled = true;
    void logeStudio.invite(m.id)
      .then((r) => {
        if (r.mailSent) showToast({ id: 0, kind: 'info', text: `Invitation envoyée à ${m.name || m.email}.` });
        else if (r.url) openInviteLink(m, r.url, () => showToast({ id: 0, kind: 'info', text: 'Lien copié. Transmettez-le à cette personne seulement.' }));
        else showToast({ id: 0, kind: 'error', text: 'L’invitation n’est pas partie : reliez Google (envoi d’e-mails) dans Préférences, puis recommencez.' });
        void refresh();
      })
      .catch((e: unknown) => showToast({ id: 0, kind: 'error', text: e instanceof Error ? e.message : 'Envoi impossible.' }))
      .finally(() => { btn.disabled = false; });
  });
  on(view, 'click', '[data-revoke]', (_e, t) => {
    const c = route.name === 'client' ? findClient(route.clientId) : undefined;
    const m = c?.members.find((x) => x.id === t.dataset.revoke);
    if (!c || !m) return;
    if (!confirm(`Retirer l’accès de ${m.name || m.email} à la Loge de ${c.name} ? Ses sessions sont fermées tout de suite ; ses notes et validations restent dans les projets.`)) return;
    void logeStudio.revokeMember(m.id)
      .then(() => { showToast({ id: 0, kind: 'info', text: 'Accès retiré.' }); void refresh(); })
      .catch((e: unknown) => showToast({ id: 0, kind: 'error', text: e instanceof Error ? e.message : 'Impossible de retirer l’accès.' }));
  });
  on(view, 'click', '[data-unlock-client]', (_e, t) => {
    const c = findClient(t.dataset.unlockClient!);
    if (!c) return;
    const open = t.dataset.lock !== 'true';
    if (!confirm(open
      ? `Débloquer les téléchargements de ${c.name} ?\n\nToutes les personnes invitées pourront télécharger les originaux de tous ses projets, y compris les prochains. À faire une fois le projet entièrement réglé.`
      : `Refermer les téléchargements de ${c.name} ?\n\nLes personnes invitées reviennent à la consultation seule (sauf les projets débloqués un par un et les fichiers marqués « Téléchargeable »). Ce qu’elles ont déjà téléchargé reste chez elles.`)) return;
    const btn = t as HTMLButtonElement;
    btn.disabled = true;
    void logeStudio.unlock({ clientId: c.id }, open)
      .then(() => { showToast({ id: 0, kind: 'info', text: open ? `Téléchargements débloqués pour ${c.name}.` : `Téléchargements refermés pour ${c.name}.` }); void refresh(); })
      .catch((e: unknown) => { btn.disabled = false; showToast({ id: 0, kind: 'error', text: e instanceof Error ? e.message : 'Réglage impossible.' }); });
  });
  on(view, 'input', '[data-reply-input]', (_e, t) => { drafts.set(t.dataset.replyInput ?? '', (t as HTMLInputElement).value); });
  on(view, 'submit', '[data-reply]', (e, t) => {
    e.preventDefault();
    const form = t as HTMLFormElement;
    const input = el<HTMLInputElement>(form, 'input');
    const body = input.value.replace(/\s+/g, ' ').trim();
    if (!body) { input.focus(); return; }
    const btn = el<HTMLButtonElement>(form, 'button');
    btn.disabled = true;
    void logeStudio.reply(form.dataset.reply!, body)
      .then((r) => {
        drafts.delete(form.dataset.reply!);
        if (form.dataset.readId) markRead([form.dataset.readId]);
        showToast({ id: 0, ...mailToast(r, 'Réponse envoyée') });
        void refresh();
      })
      .catch((err: unknown) => { btn.disabled = false; showToast({ id: 0, kind: 'error', text: err instanceof Error ? err.message : 'Réponse impossible.' }); });
  });
  // Aperçu illisible (lien expiré, pas de session client) : on garde la case, sans image cassée.
  view.addEventListener('error', (e) => {
    const img = e.target as HTMLElement;
    if (img instanceof HTMLImageElement && img.matches('[data-thumb]')) img.replaceWith(Object.assign(document.createElement('span'), { className: 'loge-th-tag', textContent: '' }));
  }, true);

  // Retour sur l'onglet : relire la boîte de réception (sans déranger une saisie ni une fenêtre ouverte).
  const onVisible = () => {
    if (!main.isConnected) { document.removeEventListener('visibilitychange', onVisible); return; }
    if (document.hidden || document.querySelector('dialog[open]')) return;
    if (route.name === 'project') { project?.refresh(); return; }
    if (view.contains(document.activeElement) && (document.activeElement as HTMLElement).matches('input, textarea')) return;
    void refresh();
  };
  document.addEventListener('visibilitychange', onVisible);

  function update(): void {
    el(main, '[data-banner]').innerHTML = syncBanner(store);
  }

  update();
  if (route.name === 'project') { render(); void loadOverview().then(() => project?.setOverview(overview)); }
  else render();
  return { update };
}

function errorBlock(message: string): string {
  return `<section class="page-head"><div><div class="eyebrow">La Loge · espace client</div><h1>Loges</h1></div></section>
    <div class="banner bad" role="alert">${icon('alert')}<div><b>Impossible de lire les Loges.</b><br>${esc(message)}</div><button class="btn small" type="button" data-retry-load>${icon('refresh')}Réessayer</button></div>`;
}
